import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';


import {BasicPage} from 'src/components/basic-page';
import {DateInput, HtmlArea, TimeInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl, ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {AlertType} from 'src/openapi/admin/models/AlertType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {AccountSearch} from '../components/account-search';
import {
    ALERT_ACCOUNT_CONTROL,
    ALERT_END_DATE_CONTROL,
    ALERT_END_TIME_CONTROL,
    ALERT_MESSAGE_CONTROL,
    ALERT_START_DATE_CONTROL,
    ALERT_TYPE_CAPTIONS, MAX_HTML_LENGTH,
} from '../constants';

import {useCreateAlert} from './useCreateAlert';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {CheckBoxChangeData} from 'src/typedef';

const CreateAlert = () => {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const {
        data: request,
        setData: setRequest,
        isChanged: isRequestChanged,
        saveChanges: createAlert,
    } = useCreateAlert();

    const {
        proceed,
        setIsChunkChanged,
    } = useConfirmationChunk({chunkName: 'create-platform-alert', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        navigate('/platform-alert-builder');
    }});

    useEffect(() => {
        setIsChunkChanged(isRequestChanged);
    }, [isRequestChanged, setIsChunkChanged]);

    const backToActiveAlerts = useCallback(() => {
        navigate('/platform-alert-builder');
    }, [navigate]);

    const changeMessage = useCallback((message: string) => {
        setRequest(prev => ({...prev, message}));
    }, [setRequest]);

    const changeAccount = useCallback((account: AccountShortInfo | null) => {
        setRequest(prev => ({...prev, account}));
    }, [setRequest]);

    const changeAlertForAllUsers = useCallback((changeData: CheckBoxChangeData) => {
        const checked = changeData.event.currentTarget.checked;
        setRequest(prev => ({
            ...prev,
            forAllUsers: checked,
            account: checked ? null : prev.account,
        }));
    }, [setRequest]);

    const changeAlertType = useCallback((alertType: AlertType) => {
        setRequest(prev => ({...prev, alertType}));
    }, [setRequest]);

    const changeStartDate = useCallback((startDate: string) => {
        setRequest(prev => ({...prev, startDate}));
    }, [setRequest]);

    const changeStartTime = useCallback((startTime: string) => {
        setRequest(prev => ({...prev, startTime}));
    }, [setRequest]);

    const changeEndDate = useCallback((endDate: string) => {
        setRequest(prev => ({...prev, endDate}));
    }, [setRequest]);

    const changeEndTime = useCallback((endTime: string) => {
        setRequest(prev => ({...prev, endTime}));
    }, [setRequest]);

    const onCreateClick = useCallback(async () => {
        setBusy(true);
        const error = await createAlert(request);
        setBusy(false);

        if (!error) {
            proceed();
        } else {
            setErrorMessage(error);
        }
    }, [request, createAlert, proceed, setErrorMessage]);

    return (
        <ServerResponseHandler
            accessRight={ACCESS_RIGHTS.PLATFORM_ALERT_BUILDER as AccessRight}
            serverError={null}>
            <BasicPage
                title="New Alert"
                buttonsBlock={(
                    <Group>
                        <Button variant="Type.TERTIARY" onClick={backToActiveAlerts}>
                            Cancel
                        </Button>
                        <Button variant="Type.PRIMARY" onClick={onCreateClick} busy={busy}>
                            Create
                        </Button>
                    </Group>
                )}>
                <Item className="group-item pd-l">
                    <Group direction="v">
                        <WidthItem width={624}>
                            <Label><RequiredMark label="Message"/></Label>
                            <ValidatedControl name={ALERT_MESSAGE_CONTROL}>
                                {() => (
                                    <HtmlArea
                                        maxCharCount={MAX_HTML_LENGTH}
                                        value={request.message}
                                        onChange={changeMessage}
                                    />
                                )}
                            </ValidatedControl>
                        </WidthItem>
                        <CheckBox
                            checked={request.forAllUsers}
                            label="For All Users"
                            onCheckBoxChange={changeAlertForAllUsers}
                        />
                        <WidthItem width={300}>
                            <Label>Account</Label>
                            <ValidatedControl name={ALERT_ACCOUNT_CONTROL}>
                                {() => (
                                    <AccountSearch
                                        disabled={request.forAllUsers}
                                        selectedAccount={request.account}
                                        onChange={changeAccount}
                                    />
                                )}
                            </ValidatedControl>
                        </WidthItem>
                        <WidthItem width={300}>
                            <Label>Type</Label>
                            <ComboBox value={request.alertType} onChange={changeAlertType}>
                                {Object.values(AlertType).map(type => (
                                    <ComboboxItem key={type} value={type} caption={ALERT_TYPE_CAPTIONS[type]}/>
                                ))}
                            </ComboBox>
                        </WidthItem>
                        <Group gap="l" alignItems="start">
                            <WidthItem width={300}>
                                <ValidatedControl name={ALERT_START_DATE_CONTROL}>
                                    {() => (
                                        <DateInput
                                            label={<RequiredMark label="Start Date"/>}
                                            id="create-alert-start-date"
                                            selectedDate={request.startDate}
                                            onChange={changeStartDate}
                                        />
                                    )}
                                </ValidatedControl>
                            </WidthItem>
                            <WidthItem width={300}>
                                <ValidatedControl name={ALERT_END_DATE_CONTROL}>
                                    {() => (
                                        <DateInput
                                            label={<RequiredMark label="End Date"/>}
                                            id="create-alert-end-date"
                                            selectedDate={request.endDate}
                                            onChange={changeEndDate}
                                        />
                                    )}
                                </ValidatedControl>
                            </WidthItem>
                        </Group>
                        <Group gap="l" alignItems="start">
                            <WidthItem width={300}>
                                <Label>Start Time</Label>
                                <TimeInput
                                    time={request.startTime}
                                    onBlur={changeStartTime}
                                />
                            </WidthItem>
                            <WidthItem width={300}>
                                <ValidatedControl name={ALERT_END_TIME_CONTROL}>
                                    {() => (
                                        <div>
                                            <Label>End Time</Label>
                                            <TimeInput
                                                time={request.endTime}
                                                onBlur={changeEndTime}
                                            />
                                        </div>
                                    )}
                                </ValidatedControl>
                            </WidthItem>
                        </Group>
                    </Group>
                </Item>
            </BasicPage>
        </ServerResponseHandler>
    );
};

export const ValidatedCreateAlert = () => (
    <ValidationContextProvider>
        <CreateAlert/>
    </ValidationContextProvider>
);
